import { CanvasAPI, ContainerAPI, UIModelAPI } from '../types/api.types';
import {
  updateHeader as updateHeaderAPI,
  updateFooter as updateFooterAPI,
  updateNavigation as updateNavigationAPI,
  updateCanvas as updateCanvasAPI,
  deleteCanvas as deleteCanvasAPI,
  bulkDeleteCanvases as bulkDeleteCanvasesAPI,
  getCanvas as getCanvasAPI, createUIModelApi, updateUIModelApi,
  getUIModelById, deleteUIModelApi, copyUIModelApi, getThemeAPI, updateThemeAPI,
  imageIconUploadAPI,
  updateSharedWidgetsAPI
} from '../services/ApiHelper';
import {UIModelTheme} from "../types/uimodel-theme.types";
import { toast } from 'Components/DesignSystem/Toast/useToast';

export class UIModelApiService {
  
  private mockUIModels: Map<string, UIModelAPI> = new Map();

  /**
   * Create UI Model
   */
  async createUIModel(configureId: string, data: FormData): Promise<{
    success: boolean;
    data: { model: UIModelAPI };
  }> {
    
    const response = await createUIModelApi(configureId, data);
    if (!response.done) {
      throw new Error(response.message ?? 'Error');
    }
    const newModel: UIModelAPI = response.data.model;
    this.mockUIModels.set(newModel.id, newModel);
    return {
      success: response.done,
      data: { model: newModel }
    };
  }
  /**
   * Create UI Model
   */
  async updateUIModel(configureId: string, modelId: string, data: FormData): Promise<{
    success: boolean;
    data: { model: UIModelAPI };
  }> {
    const response = await updateUIModelApi(configureId, modelId, data);
    if (!response.done) {
      throw new Error(response.message ?? 'Error');
    }
    const newModel: UIModelAPI = response.data.model;
    this.mockUIModels.set(newModel.id, newModel);
    return {
      success: true,
      data: { model: newModel }
    };
  }
  /**
   * Copy UI Model
   */
  async copyUIModel(configureId: string, modelId: string, data: FormData): Promise<{
    success: boolean;
    data: { model: UIModelAPI };
  }> {
    const response = await copyUIModelApi(configureId, modelId, data);
    if (!response.done) {
      throw new Error(response.message ?? 'Error');
    }
    const newModel: UIModelAPI = response.data.model;
    this.mockUIModels.set(newModel.id, newModel);
    return {
      success: true,
      data: { model: newModel }
    };
  }
/**
 * Delete UI Model
 */
async deleteUIModel(configureId: string, modelId: string): Promise<boolean> {
  try {
    const response = await deleteUIModelApi(configureId, modelId);
    if (response){
      return true; 
    }else{
      const errorMessage = "Promoted models cannot be deleted. Unpromote this model to proceed with deletion.";
      toast.error(errorMessage);
      return false; 
    }
  } catch (error) {
    console.error('Error deleting UI model:', error);
    return false;
  }
}

  /**
   * Get UI Model
   */
  async getUIModel(modelId: string, uiModelId: string): Promise<{
    success: boolean;
    data: { model: UIModelAPI };
  }> {
    //await new Promise(resolve => setTimeout(resolve, this.mockDelay));
    
    const model = this.mockUIModels.get(uiModelId);
    
    if (!model) {
      throw new Error('Model not found');
    }
    
    return {
      success: true,
      data: { model }
    };
  }

  /**
 * Update Header - Real API with FormData support
 */
async updateHeader( configureId: string,  modelId: string,  header: any,  logoFile?: File, resetButtonIconFile?: File, closeButtonIconFile?: File, saveButtonIconFile?: File): Promise<{ success: boolean; data: { updated: boolean; timestamp: string };}> {
  
  try {
    const formData = new FormData();
    
    const headerConfig = {
      showHeader: header.showHeader ?? true,
      showTitle: header.showTitle ?? true,
      titleText: header.titleText ?? '',
      titleLayout: header.titleLayout ?? 'Centre',
      showResetButton: header.showResetButton ?? true,
      resetButtonText: header.resetButtonText ?? 'Reset',
      resetButtonShowIcon: header.resetButtonShowIcon ?? false,
      resetButtonShowTooltip: header.resetButtonShowTooltip ?? false,
      resetButtonTooltipText: header.resetButtonTooltipText ?? '',
      showCloseButton: header.showCloseButton ?? true,
      closeButtonText: header.closeButtonText ?? 'Close',
      closeButtonShowIcon: header.closeButtonShowIcon ?? false,
      closeButtonShowTooltip: header.closeButtonShowTooltip ?? false,
      closeButtonTooltipText: header.closeButtonTooltipText ?? '',
      showSaveButton: header.showSaveButton ?? false,
      saveButtonText: header.saveButtonText ?? '',
      saveButtonShowIcon: header.saveButtonShowIcon ?? false,
      saveButtonShowTooltip: header.saveButtonShowTooltip ?? false,
      saveButtonTooltipText: header.saveButtonTooltipText ?? '',
      showYourBuild: header.showYourBuild ?? false,
    };

    formData.append('showHeader', headerConfig.showHeader.toString());
    formData.append('showTitle', headerConfig.showTitle.toString());
    formData.append('titleText', headerConfig.titleText);
    formData.append('titleLayout', headerConfig.titleLayout);
    formData.append('showResetButton', headerConfig.showResetButton.toString());
    formData.append('resetButtonText', headerConfig.resetButtonText);
    formData.append('resetButtonShowIcon', headerConfig.resetButtonShowIcon.toString());
    formData.append('resetButtonShowTooltip', headerConfig.resetButtonShowTooltip.toString());
    formData.append('resetButtonTooltipText', headerConfig.resetButtonTooltipText);
    formData.append('showCloseButton', headerConfig.showCloseButton.toString());
    formData.append('closeButtonText', headerConfig.closeButtonText);
    formData.append('closeButtonShowIcon', headerConfig.closeButtonShowIcon.toString());
    formData.append('closeButtonShowTooltip', headerConfig.closeButtonShowTooltip.toString());
    formData.append('closeButtonTooltipText', headerConfig.closeButtonTooltipText);

    formData.append('showSaveButton', headerConfig.showSaveButton.toString());
    formData.append('saveButtonText', headerConfig.saveButtonText);
    formData.append('saveButtonShowIcon', headerConfig.saveButtonShowIcon.toString());
    formData.append('saveButtonShowTooltip', headerConfig.saveButtonShowTooltip.toString());
    formData.append('saveButtonTooltipText', headerConfig.saveButtonTooltipText);
    formData.append('showYourBuild', headerConfig.showYourBuild.toString());

    if (logoFile) {
      formData.append('logoUrl', logoFile, logoFile.name);
    } else if (header.logoUrl) {  
      formData.append('logoUrl', header.logoUrl);
    }
    
    if (resetButtonIconFile) {
      formData.append('resetButtonIconUrl', resetButtonIconFile, resetButtonIconFile.name);
    } else if (header.resetButtonIconUrl) {
      formData.append('resetButtonIconUrl', header.resetButtonIconUrl);
    }
    
    if (closeButtonIconFile) {
      formData.append('closeButtonIconUrl', closeButtonIconFile, closeButtonIconFile.name);
    } else if (header.closeButtonIconUrl) {
      formData.append('closeButtonIconUrl', header.closeButtonIconUrl);
    }

    if (saveButtonIconFile) {
      formData.append('saveButtonIconUrl', saveButtonIconFile, saveButtonIconFile.name);
    } else if (header.saveButtonIconUrl) {
      formData.append('saveButtonIconUrl', header.saveButtonIconUrl);
    }
    
    await updateHeaderAPI(configureId, modelId, formData);
    
    return {
      success: true,
      data: {
        updated: true,
        timestamp: new Date().toISOString()
      }
    };
  } catch (error) {
    console.error('Error updating header:', error);
    throw error;
  }
}

/**
 * Update Footer - Real API with FormData support
 */
async updateFooter( configureId: string,  modelId: string,  footer: any,  previousButtonIconFile?: File, nextButtonIconFile?: File, doneButtonIconFile?: File ): Promise<{ success: boolean; data: { updated: boolean; timestamp: string };}> {

  try {
    const formData = new FormData();

    const footerConfig = {
      showFooter: footer.showFooter ?? true,
      showDescription: footer.showDescription ?? false,
      descriptionText: footer.descriptionText ?? '',
      showPreviousButton: footer.showPreviousButton ?? true,
      previousButtonText: footer.previousButtonText ?? 'Previous',
      previousButtonShowIcon: footer.previousButtonShowIcon ?? false,
      previousButtonShowTooltip: footer.previousButtonShowTooltip ?? false,
      previousButtonTooltipText: footer.previousButtonTooltipText ?? '',
      showNextButton: footer.showNextButton ?? true,
      nextButtonText: footer.nextButtonText ?? 'Next',
      nextButtonShowIcon: footer.nextButtonShowIcon ?? false,
      nextButtonShowTooltip: footer.nextButtonShowTooltip ?? false,
      nextButtonTooltipText: footer.nextButtonTooltipText ?? '',
      showDone: footer.showDone ?? false,
      doneButtonText: footer.doneButtonText ?? 'Done',
      doneButtonShowIcon: footer.doneButtonShowIcon ?? false,
      doneButtonShowTooltip: footer.doneButtonShowTooltip ?? false,
      doneButtonTooltipText: footer.doneButtonTooltipText ?? '',
    };

    formData.append('showFooter', footerConfig.showFooter.toString());
    formData.append('showDescription', footerConfig.showDescription.toString());
    formData.append('descriptionText', footerConfig.descriptionText);
    formData.append('showPreviousButton', footerConfig.showPreviousButton.toString());
    formData.append('previousButtonText', footerConfig.previousButtonText);
    formData.append('previousButtonShowIcon', footerConfig.previousButtonShowIcon.toString());
    formData.append('previousButtonShowTooltip', footerConfig.previousButtonShowTooltip.toString());
    formData.append('previousButtonTooltipText', footerConfig.previousButtonTooltipText);
    formData.append('showNextButton', footerConfig.showNextButton.toString());
    formData.append('nextButtonText', footerConfig.nextButtonText);
    formData.append('nextButtonShowIcon', footerConfig.nextButtonShowIcon.toString());
    formData.append('nextButtonShowTooltip', footerConfig.nextButtonShowTooltip.toString());
    formData.append('nextButtonTooltipText', footerConfig.nextButtonTooltipText);
    formData.append('showDone', footerConfig.showDone.toString());
    formData.append('doneButtonText', footerConfig.doneButtonText);
    formData.append('doneButtonShowIcon', footerConfig.doneButtonShowIcon.toString());
    formData.append('doneButtonShowTooltip', footerConfig.doneButtonShowTooltip.toString());
    formData.append('doneButtonTooltipText', footerConfig.doneButtonTooltipText);

    if (previousButtonIconFile) {
      formData.append('previousButtonIconUrl', previousButtonIconFile, previousButtonIconFile.name);
    } else if (footer.previousButtonIconUrl) {
      formData.append('previousButtonIconUrl', footer.previousButtonIconUrl);
    }

    if (nextButtonIconFile) {
      formData.append('nextButtonIconUrl', nextButtonIconFile, nextButtonIconFile.name);
    } else if (footer.nextButtonIconUrl) {
      formData.append('nextButtonIconUrl', footer.nextButtonIconUrl);
    }

    if (doneButtonIconFile) {
      formData.append('doneButtonIconUrl', doneButtonIconFile, doneButtonIconFile.name);
    } else if (footer.doneButtonIconUrl) {
      formData.append('doneButtonIconUrl', footer.doneButtonIconUrl);
    }

    await updateFooterAPI(configureId, modelId, formData);

    return {
      success: true,
      data: {
        updated: true,
        timestamp: new Date().toISOString()
      }
    };
  } catch (error) {
    console.error('Error updating footer:', error);
    throw error;
  }
}

  /**
   * Update Navigation - Real API
   */
  async updateNavigation(configureId: string, modelId: string, navigation: any): Promise<{
    success: boolean;
    data: { updated: boolean; timestamp: string };
}> {
    console.log('Updating navigation:', { configureId, modelId, navigation });
    if (!navigation?.navigation?.groups?.length) {
        return {
            success: false,
            data: {
                updated: false,
                timestamp: new Date().toISOString()
            }
        };
    }

    try {
        const response = await updateNavigationAPI(configureId, modelId, navigation);

        if (response && response.done) {
          return {
              success: true,
              data: {
              updated: response?.data?.updated ?? true,
              timestamp: response?.data?.updatedAt || new Date().toISOString()
              }
          };
        }

        throw new Error(response?.message || 'Failed to update navigation');
    } catch (error) {
        console.error('Error updating navigation:', error);
        throw error;
    }
}

  /**
   * Get Canvas by ID - Real API
   */
  async getCanvas(configureId: string, modelId: string, canvasId: string): Promise<{
  success: boolean;
  data: { 
    canvas: {
      pageId: string;
      groupId: string;
      containers: ContainerAPI[];
    } 
  };
}> {
  
  try {
    const response = await getCanvasAPI(configureId, modelId, canvasId);
    
    if (response && response.data) {
      const containers = (response.data.containers || []).map((container: any) => ({
        ...container,
        content: {
          items: [],
          ...container.content  
        }
      }));
      
      return {
        success: true,
        data: {
          canvas: {
            pageId: response.data.pageId || '',
            groupId: response.data.groupId || '',
            containers: containers
          }
        }
      };
    }
    
    throw new Error(`Canvas ${canvasId} not found`);
  } catch (error) {
    console.error('Error fetching canvas:', error);
    return {
      success: true,
      data: {
        canvas: {
          pageId: '',
          groupId: '',
          containers: []
        }
      }
    };
  }
}

/**
 * Get Canvas by ID
 */
    async getCanvasById(modelId: string, uiModelId: string, canvasId: string): Promise<{
    success: boolean;
    data: { canvas: CanvasAPI };
    }> {
        
        const mockCanvas: CanvasAPI = {
            pageId: '',
            groupId: '',
            containers: []
        };
        
        
        return {
            success: true,
            data: { canvas: mockCanvas }
        };
    }

    async getUIModelById(configureId: string, modelId: string): Promise<{ success: boolean; data: { model: UIModelAPI };}> {
      try {
        const response = await getUIModelById(configureId, modelId);
        
        if (response && response.done) {
          return {
            success: true,
            data: { model: response.data.model }
          };
        }
        
        throw new Error('Failed to fetch UI model');
      } catch (error) {
        console.error('Error fetching UI model:', error);
        throw error;
      }
    } 

    /**
     * Delete Canvas
     */
    async deleteCanvas(configureId: string, modelId: string, canvasId: string): Promise<{ success: boolean }> {
      try {
        await deleteCanvasAPI(configureId, modelId, canvasId);
        return { success: true };
      } catch (error) {
        console.error('Error deleting canvas:', error);
        return { success: false };
      }
    }

    /**
     * Bulk Delete Canvases
     */
    async bulkDeleteCanvases(configureId: string, modelId: string, canvasIds: string[]): Promise<{ success: boolean; deleted?: string[]; failed?: string[] }> {
      try {
        const result = await bulkDeleteCanvasesAPI(configureId, modelId, canvasIds);
        return { success: true, deleted: result?.deleted || [], failed: result?.failed || [] };
      } catch (error) {
        console.error('Error bulk deleting canvases:', error);
        return { success: false, deleted: [], failed: canvasIds };
      }
    }

    /**
     * Update Canvas
     */
    async updateCanvas(configureId: string, modelId: string, canvas: any, canvasId: string): Promise<{
      success: boolean;
      data: { updated: boolean; timestamp: string };}> {

        
        try {
          await updateCanvasAPI(configureId, modelId, canvasId, canvas);
          
          return {
            success: true,
            data: {
              updated: true,
              timestamp: new Date().toISOString()
            }
          };
        } catch (error) {
          console.error('Error updating canvas:', error);
          throw error;
        }
    }

    /**
     * Get Theme
     */
    async getTheme(projectId: string, modelId: string): Promise<{ success: boolean; data: UIModelTheme;}> {
      const response = await getThemeAPI(projectId, modelId);
      if (!response.done) {
        throw new Error(response.message ?? 'Error');
      }
      const uiModelThemeModel: UIModelTheme = response.data;
      return {
        success: response.done,
        data: uiModelThemeModel
      };
    }

    /**
     * Get Theme
     */
    async updateTheme(projectId: string, modelId: string, data: UIModelTheme | null): Promise<{ success: boolean;}> {
      const response = await updateThemeAPI(projectId, modelId, data);
      if (!response.done) {
        throw new Error(response.message ?? 'Error');
      }
      return {
        success: response.done,
      };
    }

    /**
     * Update Shared Widgets - model-level widget configurations
     */
    async updateSharedWidgets(projectId: string, modelId: string, data: any): Promise<{ success: boolean;}> {
      try {
        const response = await updateSharedWidgetsAPI(projectId, modelId, data);
        // Handle case where backend returns undefined (endpoint not yet implemented)
        if (!response) {
          console.log('Shared widgets endpoint not yet available, skipping save');
          return { success: true };
        }
        if (!response.done) {
          throw new Error(response.message ?? 'Error updating shared widgets');
        }
        return {
          success: response.done,
        };
      } catch (error) {
        console.error('Error updating shared widgets:', error);
        // Don't throw - allow graceful degradation until backend is ready
        return { success: false };
      }
    }


    async uploadImageIcon (projectId: string, modelId: string, file: File, imageType: string = 'icon'): Promise<{ success: boolean; data: { imageUrl: string };}> {
      const response = await imageIconUploadAPI(projectId, modelId, file, imageType);

      // Extract imageUrl from nested response structure
      const imageUrl = response?.data?.imageUrl || '';

      return {
        success: true,
        data: { imageUrl }
      };
    }
}

export const uiModelApiService = new UIModelApiService();